import {
  Colors,
  DashboardStatItem,
  IcAddress,
  IcClose,
  IcOnline,
  IcPhone,
  IcInStore,
  Text,
  IcScreen,
} from 'basic-ui-lib';
import storeSelectors from 'modules/store/services/selectors';
import { ScreenStatus } from 'modules/dashboard/services/constants';
import styled from 'styled-components';

interface StoreInfoProps {
  storeName?: string;
  ownerName?: string;
  phone?: string;
  address?: string;
  totalScreen?: string | number;
}

const StoreInfo = ({ storeName, ownerName, phone, address, totalScreen }: StoreInfoProps) => {
  const screenList = storeSelectors.getScreenListInStore();
  const onlineCount =
    screenList?.content?.filter(s => s.status === ScreenStatus.ONLINE).length ?? 0;
  const offlineCount =
    screenList?.content?.filter(
      s => s.status === ScreenStatus.OFFLINE || s.status === ScreenStatus.DEACTIVATED,
    ).length ?? 0;

  return (
    <>
      <HeaderSection>
        <AvatarBox>
          <IcInStore width={28} height={28} fill={Colors.neutral.bg[1]} />
        </AvatarBox>
        <NameGroup>
          <Text title_h5 semibold left>
            {storeName || '—'}
          </Text>
          <OwnerText>OWNER: {ownerName || '—'}</OwnerText>
        </NameGroup>
      </HeaderSection>

      <MiddleSection>
        <InfoGroup>
          <InfoRow>
            <IconBox>
              <IcPhone width={16} height={16} fill={Colors.primary[6]} />
            </IconBox>
            <InfoContent>
              <InfoLabel>PHONE</InfoLabel>
              <Text content_2 medium left>
                {phone || '—'}
              </Text>
            </InfoContent>
          </InfoRow>
          <InfoRow>
            <IconBox>
              <IcAddress width={16} height={16} fill={Colors.primary[6]} />
            </IconBox>
            <InfoContent>
              <InfoLabel>ADDRESS</InfoLabel>
              <Text content_2 medium left>
                {address || '—'}
              </Text>
            </InfoContent>
          </InfoRow>
        </InfoGroup>

        <StatsGroup>
          <DashboardStatItem
            label='Total Screens'
            value={totalScreen ?? screenList?.totalElements ?? 0}
            icon={<IcScreen width={22} height={22} fill={Colors.primary[6]} />}
            backgroundColorIcon={Colors.primary[2]}
          />
          <DashboardStatItem
            label='Online Screens'
            value={onlineCount}
            icon={<IcOnline width={22} height={22} fill={Colors.secondary[6]} />}
            backgroundColorIcon={Colors.secondary[1]}
          />
          <DashboardStatItem
            label='Offline Screens'
            value={offlineCount}
            icon={<IcClose fill={Colors.other[2]} width={22} height={22} />}
            backgroundColorIcon={Colors.other[1]}
          />
        </StatsGroup>
      </MiddleSection>
    </>
  );
};

export default StoreInfo;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${Colors.neutral.border[1]};
`;

const AvatarBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${Colors.primary[5]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NameGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const OwnerText = styled.p`
  font-size: 12px;
  color: ${Colors.neutral.text[3]};
  text-align: left;
  margin: 0;
  font-family: 'Poppins';
  font-weight: 500;
`;

const MiddleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${Colors.neutral.border[1]};
  background: ${Colors.neutral.bg[2]};
  flex: 1;
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${Colors.primary[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  color: ${Colors.neutral.text[3]};
  font-family: 'Poppins';
  font-weight: 500;
  letter-spacing: 0.4px;
  text-align: left;
`;

const StatsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
